import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series (based on actual games in project)
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    // Slope Series
    'slope-game': ['slope-ball', 'slope-city', 'slope-plus', 'slope-racing-3d', 'slope-tunnel', 'slope-2', 'slope-3'],
    'slope-ball': ['slope-game', 'slope-city', 'slope-plus', 'slope-racing-3d', 'slope-tunnel', 'slope-2', 'slope-3'],
    'slope-city': ['slope-game', 'slope-ball', 'slope-plus', 'slope-racing-3d', 'slope-tunnel', 'slope-2', 'slope-3'],
    'slope-plus': ['slope-game', 'slope-ball', 'slope-city', 'slope-racing-3d', 'slope-tunnel', 'slope-2', 'slope-3'],
    'slope-racing-3d': ['slope-game', 'slope-ball', 'slope-city', 'slope-plus', 'slope-tunnel', 'slope-2', 'slope-3'],
    'slope-tunnel': ['slope-game', 'slope-ball', 'slope-city', 'slope-plus', 'slope-racing-3d', 'slope-2', 'slope-3'],
    'slope-2': ['slope-game', 'slope-ball', 'slope-city', 'slope-plus', 'slope-racing-3d', 'slope-tunnel', 'slope-3'],
    'slope-3': ['slope-game', 'slope-ball', 'slope-city', 'slope-plus', 'slope-racing-3d', 'slope-tunnel', 'slope-2'],

    // Subway Surfers Series
    'subway-surfers': ['subway-surfers-bali', 'subway-surfers-havana', 'subway-surfers-mumbai', 'subway-surfers-new-orleans', 'subway-surfers-singapore', 'subway-surfers-venice', 'subway-surfers-winter-holiday'],
    'subway-surfers-bali': ['subway-surfers', 'subway-surfers-havana', 'subway-surfers-mumbai', 'subway-surfers-new-orleans', 'subway-surfers-singapore', 'subway-surfers-venice', 'subway-surfers-winter-holiday'],
    'subway-surfers-havana': ['subway-surfers', 'subway-surfers-bali', 'subway-surfers-mumbai', 'subway-surfers-new-orleans', 'subway-surfers-singapore', 'subway-surfers-venice', 'subway-surfers-winter-holiday'],
    'subway-surfers-mumbai': ['subway-surfers', 'subway-surfers-bali', 'subway-surfers-havana', 'subway-surfers-new-orleans', 'subway-surfers-singapore', 'subway-surfers-venice', 'subway-surfers-winter-holiday'],
    'subway-surfers-new-orleans': ['subway-surfers', 'subway-surfers-bali', 'subway-surfers-havana', 'subway-surfers-mumbai', 'subway-surfers-singapore', 'subway-surfers-venice', 'subway-surfers-winter-holiday'],
    'subway-surfers-singapore': ['subway-surfers', 'subway-surfers-bali', 'subway-surfers-havana', 'subway-surfers-mumbai', 'subway-surfers-new-orleans', 'subway-surfers-venice', 'subway-surfers-winter-holiday'],
    'subway-surfers-venice': ['subway-surfers', 'subway-surfers-bali', 'subway-surfers-havana', 'subway-surfers-mumbai', 'subway-surfers-new-orleans', 'subway-surfers-singapore', 'subway-surfers-winter-holiday'],
    'subway-surfers-winter-holiday': ['subway-surfers', 'subway-surfers-bali', 'subway-surfers-havana', 'subway-surfers-mumbai', 'subway-surfers-new-orleans', 'subway-surfers-singapore', 'subway-surfers-venice'],

    // VEX Series
    'vex3': ['vex4', 'vex5', 'vex6', 'vex7'],
    'vex4': ['vex3', 'vex5', 'vex6', 'vex7'],
    'vex5': ['vex3', 'vex4', 'vex6', 'vex7'],
    'vex6': ['vex3', 'vex4', 'vex5', 'vex7'],
    'vex7': ['vex3', 'vex4', 'vex5', 'vex6'],

    // Stickman Fighter Series
    'stickman-fighter': ['stickman-fighter-epic-battle-2'],
    'stickman-fighter-epic-battle-2': ['stickman-fighter'],

    // Moto X3M Series
    'moto-x3m': ['motox3m-2', 'motox3m-3', 'moto-x3m-4-winter', 'moto-x3m-5-pool-party', 'moto-x3m-spooky-land'],
    'motox3m-2': ['moto-x3m', 'motox3m-3', 'moto-x3m-4-winter', 'moto-x3m-5-pool-party', 'moto-x3m-spooky-land'],
    'motox3m-3': ['moto-x3m', 'motox3m-2', 'moto-x3m-4-winter', 'moto-x3m-5-pool-party', 'moto-x3m-spooky-land'],
    'moto-x3m-4-winter': ['moto-x3m', 'motox3m-2', 'motox3m-3', 'moto-x3m-5-pool-party', 'moto-x3m-spooky-land'],
    'moto-x3m-5-pool-party': ['moto-x3m', 'motox3m-2', 'motox3m-3', 'moto-x3m-4-winter', 'moto-x3m-spooky-land'],
    'moto-x3m-spooky-land': ['moto-x3m', 'motox3m-2', 'motox3m-3', 'moto-x3m-4-winter', 'moto-x3m-5-pool-party'],

    // The Impossible Quiz Series
    'the-impossible-quiz': ['the-impossible-quiz-2'],
    'the-impossible-quiz-2': ['the-impossible-quiz'],

    // Worlds Hardest Game Series  
    'worlds-hardest-game': ['worlds-hardest-game-2', 'worlds-hardest-game-3'],
    'worlds-hardest-game-2': ['worlds-hardest-game', 'worlds-hardest-game-3'],
    'worlds-hardest-game-3': ['worlds-hardest-game', 'worlds-hardest-game-2'],

    // Tunnel Rush Series
    'tunnel-rush': ['tunnel-rush-2'],
    'tunnel-rush-2': ['tunnel-rush'],

    // Two Ball Series
    'two-ball-3d': ['two-ball-3d-dark'],
    'two-ball-3d-dark': ['two-ball-3d'],

    // Temple Series
    'temple-of-boom': ['temple-run-2'],
    'temple-run-2': ['temple-of-boom'],

    // Drive Mad Series (similar driving games)
    'drive-mad': ['drive-mad-2', 'drive-mad-3', 'drive-mad-4'],
    'drive-mad-2': ['drive-mad', 'drive-mad-3', 'drive-mad-4'],
    'drive-mad-3': ['drive-mad', 'drive-mad-2', 'drive-mad-4'],
    'drive-mad-4': ['drive-mad', 'drive-mad-2', 'drive-mad-3'],

    // Geometry Dash Series
    'geometry-dash': ['geometry-dash-2', 'geometry-dash-lite', 'geometry-dash-world'],
    'geometry-dash-2': ['geometry-dash', 'geometry-dash-lite', 'geometry-dash-world'],
    'geometry-dash-lite': ['geometry-dash', 'geometry-dash-2', 'geometry-dash-world'],
    'geometry-dash-world': ['geometry-dash', 'geometry-dash-2', 'geometry-dash-lite'],

    // Monkey Mart Series (if exists)
    'monkey-mart': ['monkey-mart-2'],
    'monkey-mart-2': ['monkey-mart'],

    // Papa's Series
    'papas-pizzeria': ['papas-burgeria', 'papas-freezeria'],
    'papas-burgeria': ['papas-pizzeria', 'papas-freezeria'],
    'papas-freezeria': ['papas-pizzeria', 'papas-burgeria'],

    // Fireboy and Watergirl Series
    'fireboy-and-watergirl': ['fireboy-and-watergirl-2', 'fireboy-and-watergirl-3'],
    'fireboy-and-watergirl-2': ['fireboy-and-watergirl', 'fireboy-and-watergirl-3'],
    'fireboy-and-watergirl-3': ['fireboy-and-watergirl', 'fireboy-and-watergirl-2']
  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar based on new categories
    const priorityGames = [
      'slope',
      'blumgi-rocket',
      'slope-tunnel',
      'moto-x3m-4-winter',
      'drive-mad',
      'retro-bowl',
      'eggy-car',
      'drift-hunters',
      'doodle-jump',
      '2048',
      'subway-surfers',
      'stickman-hook',
      'among-us',
      'adventure-drivers',
      '10-minutes-till-dawn'
    ];

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
